import React, { useState, useEffect } from "react";
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import useTrackedRealtimeEdits from '../../../realtime/useTrackedRealtimeEdits';
import { formActivity } from '../../../realtime/formActivity';
import { useSelector, useDispatch } from "react-redux";
import { selectUserDetails } from "../../../redux/slices/userSlice";
import { selectJobChangeOrderDetails, selectJobId, selectGeneralProject, selectStationEnclosure } from "../../../redux/selectors";
import { selectDropdownOptions } from "../../../redux/slices/dropdownSlice";
import { Container, Row, Col, Form, Modal, Button } from 'react-bootstrap';
import { patchData, postData, deleteData } from '../../../redux/slices/apiCallSlice';
import { postChangeOrderDetail } from "../../../redux/slices/postChangeOrderDetailSlice";

import { clearSectionJobDetails } from "../../../redux/slices/jobDetailsSlice";
import { deleteChangeOrderItem } from "../../../redux/slices/changeOrderSlice";
import { fetchFullJob } from "../../../redux/slices/fullJobFetchSlice";
import EditChangeOrder from './EditChangeOrder';
import SendChangeOrder from './SendChangeOrder';
import { formatDateForAPI } from "../../../utils/dateUtils";

const ChangeOrderItems = ({ onItemDeleted, deletedItemsIds }) => {
    const jobId = useSelector(selectJobId);
    const userDetails = useSelector(selectUserDetails);
    const currentUser = userDetails?.name;
    const options = useSelector(selectDropdownOptions); // Fetch dropdown values from criticalDataSlice
    const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
    const dispatch = useDispatch();
    const projectDetails = useSelector(selectGeneralProject);
    const [localChangeOrderDetails, setLocalChangeOrderDetails] = useState([]);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateTitles, setDuplicateTitles] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [notes, setNotes] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [userSelection, setUserSelection] = useState(null); // Track user selection for combining duplicates
    const [newChangeOrder, setNewChangeOrder] = useState({
        Title: "",
        ReasonForChange: "",
        Change: ""
    });
    const enclosureDetails = useSelector(selectStationEnclosure);
    const [impactsCSM, setImpactsCSM] = useState(enclosureDetails.some(enclosure => enclosure.HasEnclosure));
    const [includeAllControls, setIncludeAllControls] = useState(false);
    const [showAllItems, setShowAllItems] = useState(false); // Toggle to show all items including resolved
    const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false); // Track loading state for duplicate combining
    const realtimeEdits = useTrackedRealtimeEdits();
    useRealtimeDraft(showDuplicateModal || showEditModal || isPreviewMode || showAddModal || isLoadingDuplicates || Boolean(notes));

    useEffect(() => {
        // Fetch the latest full job data when component loads
        dispatch(fetchFullJob({ jobId }));
    }, [jobId, dispatch]);

    useEffect(() => {
        // Filter for pending items only (those WITHOUT ChangeOrderNumber key)
        // These are items that haven't been published to a change order yet
        let filteredDetails = changeOrderDetails
            .filter(detail => detail.ProjectID === jobId)
            .filter(detail => !detail.hasOwnProperty('ChangeOrderNumber')) // Only pending items without ChangeOrderNumber
            .filter(detail => !deletedItemsIds || !deletedItemsIds.has(detail.ID)); // Filter out known deleted items
        
        // If not showing all items, exclude resolved items
        if (!showAllItems) {
            filteredDetails = filteredDetails.filter(detail => detail.Status !== "Resolved");
        }
        
        setLocalChangeOrderDetails(filteredDetails);
        if (userSelection === null) {
            checkForDuplicateTitles(filteredDetails);
        }
    }, [changeOrderDetails, jobId, userSelection, deletedItemsIds, showAllItems]);


    const reasonForChangeOptions = options?.filter((option) => option.DropdownType === "Reason For Change") || [];
    const billCreditToOptions = options?.filter((option) => option.DropdownType === "Bill/ Credit To") || [];

    const handleCheckboxChange = (detailId, field, value) => {
        const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
        const data = { [field]: String(value), ID: detailId };
    
        if (field === 'FollowUpRequired') {
            data.Status = value ? 'Follow Up' : 'Pending';
        }        // Handle assigned fields for specific checkboxes
        if (field === 'ActionRequiredMechanical') {
            data.AssignedMechanical = value ? projectDetails[0].MechanicalEngineer : null;
        } else if (field === 'ActionRequiredElectrical') {
            data.AssignedElectrical = value ? projectDetails[0].ElectricalEngineer : null;
        } else if (field === 'ActionRequiredControls') {
            data.AssignedControls = value ? projectDetails[0].ControlsEngineer : null;
        } else if (field === 'ActionRequiredCSM') {
            data.AssignedNP = value ? "david.neesen@watertronics.com" : null;
        }
    
        realtimeEdits.track(data.ID, data, dispatch(patchData({ url, data })));
    
        setLocalChangeOrderDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.ID === detailId ? { ...detail, [field]: value, Status: data.Status } : detail
            )
        );
    };

    // Handle local state changes without API calls
    const handleLocalChange = (detailId, field, value) => {
        realtimeEdits.mark(`${detailId}:${field}`);
        setLocalChangeOrderDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.ID === detailId ? { ...detail, [field]: value } : detail
            )
        );
    };

    // Handle input change which should only trigger API calls on blur
    const handleTitleChange = (detailId, value) => {
        handleLocalChange(detailId, 'Title', value);
    };

    // Handle input blur which triggers API calls
    const handleTitleBlur = (detailId, value) => {
        // Get the original change order detail to compare values
        const originalDetail = changeOrderDetails.find(detail => detail.ID === detailId);
        
        // Only make API call if the value has actually changed
        if (originalDetail && originalDetail.Title !== value) {
            const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
            const data = { Title: value === "" ? "BLANK" : value, ID: detailId };

            realtimeEdits.track(data.ID, data, dispatch(patchData({ url, data })));
        } else {
            realtimeEdits.clear(`${detailId}:Title`);
        }
    };

    const handleInputChange = (detailId, field, value) => {
        const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
        const data = { [field]: value === "" ? "BLANK" : value, ID: detailId };

        realtimeEdits.track(data.ID, data, dispatch(patchData({ url, data })));

        setLocalChangeOrderDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.ID === detailId ? { ...detail, [field]: value } : detail
            )
        );
    };

    const handleDelete = async (detailId) => {
        const finishRealtimeWrite = formActivity.saving();
        // Optimistic update - remove the item from local state immediately
        setLocalChangeOrderDetails(prevDetails => 
            prevDetails.filter(detail => detail.ID !== detailId)
        );
        
        try {
            // Use the deleteChangeOrderItem thunk, which uses our centralized axios instance with retry
            await dispatch(deleteChangeOrderItem(detailId)).unwrap();
            console.log(`Change order item ${detailId} deleted successfully`);
            
            // If deletion was successful, update the parent container's tracking of deleted items
            if (onItemDeleted) {
                onItemDeleted(detailId);
            }
            
            // No need to show success message since the item is already removed from the UI
        } catch (error) {
            console.error("Failed to delete change order item:", error);
            
            // If the error object doesn't exist or doesn't have expected properties,
            // it might actually be a successful deletion with unexpected response format
            if (!error || (error && typeof error === 'object' && Object.keys(error).length === 0)) {
                console.log("Empty error object, likely a successful deletion");
                // Still track the deletion since it was probably successful
                if (onItemDeleted) {
                    onItemDeleted(detailId);
                }
                return; // Consider this a success
            }
            
            // Don't show error for items that don't exist or were already deleted
            if (error?.message?.includes('not found')) {
                console.log("Item already deleted or doesn't exist, not showing error");
                // Still track the deletion since the item doesn't exist anyway
                if (onItemDeleted) {
                    onItemDeleted(detailId);
                }
                return;
            }
            
            // If we get here, it's a genuine error - show message and restore item
            alert("Failed to delete the item. Please try again later.");
            
            // Restore the deleted item in the local state
            const originalItem = changeOrderDetails.find(detail => detail.ID === detailId);
            if (originalItem) {
                setLocalChangeOrderDetails(prevDetails => [...prevDetails, originalItem]);
            }
            
        } finally { finishRealtimeWrite(); }
    };

    const checkForDuplicateTitles = (details) => {
        const titles = details
            .filter(detail => detail.Status !== "Resolved") // Exclude resolved items
            .map(detail => detail.Title);
        const duplicates = titles.filter((title, index) => titles.indexOf(title) !== index);
        if (duplicates.length > 0) {
            setDuplicateTitles(duplicates);
            setShowDuplicateModal(true);
        }
    };

    const handleCombineDuplicates = async () => {
        setUserSelection(true); // Save user selection
        setIsLoadingDuplicates(true); // Show loading state

        try {
            const processedTitles = new Set(); // Track processed titles to avoid duplicate deletions

            for (const title of duplicateTitles) {
                if (processedTitles.has(title)) continue; // Skip if already processed
                processedTitles.add(title);

                const duplicates = localChangeOrderDetails.filter(detail => detail.Title === title);
                
                // First check for any items where existingValue equals newValue and delete those
                for (const detail of duplicates) {
                    // Normalize values before comparison to handle null/undefined/"BLANK" cases
                    const existingValue = detail.ExistingValue || "BLANK";
                    const newValue = detail.NewValue || "BLANK";
                    
                    if (existingValue === newValue) {
                        console.log(`Deleting change order item ${detail.ID} because existing value equals new value`);
                        await dispatch(deleteData({ 
                            url: `${process.env.REACT_APP_API_URL}/change-order-details/`, 
                            data: { ID: detail.ID },
                        })).unwrap();
                        
                        // If deletion was successful, update the parent container's tracking of deleted items
                        if (onItemDeleted) {
                            onItemDeleted(detail.ID);
                        }
                    }
                }
                
                // Filter out items that were deleted due to matching values
                const remainingDuplicates = duplicates.filter(detail => {
                    const existingValue = detail.ExistingValue || "BLANK";
                    const newValue = detail.NewValue || "BLANK";
                    return existingValue !== newValue;
                });
                
                // Only proceed with combining if there are still duplicates left
                if (remainingDuplicates.length > 0) {
                    const newDetail = {
                        Title: title,
                        ExistingValue: remainingDuplicates.reduce((min, detail) => detail.ID < min.ID ? detail : min, remainingDuplicates[0]).ExistingValue,
                        NewValue: remainingDuplicates.reduce((max, detail) => detail.ID > max.ID ? detail : max, remainingDuplicates[0]).NewValue,
                        ProjectID: jobId,
                        Status: "Pending"
                    };

                    if (remainingDuplicates.some(detail => detail.Status === 'Follow Up')) {
                        newDetail.ReasonForChange = "Resolution of issue(s) presented in previous change order";
                        newDetail.FollowUpClosed = "true";
                    }

                    await dispatch(postData({ url: `${process.env.REACT_APP_API_URL}/change-order-details`, data: newDetail })).unwrap();

                    for (const detail of remainingDuplicates) {
                        await dispatch(deleteData({
                            url: `${process.env.REACT_APP_API_URL}/change-order-details/`,
                            data: { ID: detail.ID },
                        })).unwrap();
                    }
                }
            }

            // Clear the Redux section state to remove old items before refetching
            dispatch(clearSectionJobDetails("changeOrderDetails"));
            
            // Refetch the full job data to get updated change order details
            await dispatch(fetchFullJob({ jobId }));
            
            // Close the modal once all operations complete
            setShowDuplicateModal(false);
        } catch (error) {
            console.error("Failed to combine duplicate change order items:", error);
            setUserSelection(null);
            alert("Failed to combine duplicate change order items. Please try again.");
        } finally {
            setIsLoadingDuplicates(false);
        }
    };

    const handleTogglePreview = async () => {
        // If switching TO preview mode, refresh data first
        if (!isPreviewMode) {
            console.log('Refreshing change order data before preview...');
            try {
                await dispatch(fetchFullJob({ jobId })).unwrap();
                console.log('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
            }
        }
        setIsPreviewMode(!isPreviewMode);
    };

    const handleAddChangeOrder = async () => {
        const changeOrderDetail = {
            projectID: jobId,
            currentUser: currentUser,
            userRole: userDetails?.role || "BLANK",
            section: "Change Order",
            field: newChangeOrder.Title || "BLANK",
            prevValue: "",
            newValue: newChangeOrder.Change || "BLANK",
            dateTime: formatDateForAPI(new Date()),
            title: `${newChangeOrder.Title || "BLANK"} (Change Order)`,
            status: "Pending",
        };

        try {
            // Post the new change order detail
            await dispatch(postChangeOrderDetail(changeOrderDetail)).unwrap();
            
            setShowAddModal(false);
            setNewChangeOrder({ Title: "", ReasonForChange: "", Change: "" });
            
            // Clear the Redux state and fetch fresh data to avoid duplicates
            dispatch(clearSectionJobDetails("changeOrderDetails"));
            await dispatch(fetchFullJob({ jobId }));
        } catch (error) {
            console.error("Error adding change order:", error);
            alert("Failed to add change order. Please try again.");
        }
    };

    // Use loose equality (==) for ChangeOrderNumber checks to handle both null and undefined from fullJobFetch API
    const isPopulateDisabled = localChangeOrderDetails.some(detail => 
        !detail.FollowUpRequired && detail.ImpactsCSO && !detail.CSOUpdated
    );

    const handleDuplicateModalClose = () => {
        setShowDuplicateModal(false);
        setUserSelection(false); // Save user selection
    };

    const handleModalCloseAfterSubmit = () => {
        setShowEditModal(false); // Close the modal
    };

    return (
        <Container className="change-order-items">
            <h2>Change Order Items</h2>
            <div className="mb-3 mt-2 change-order-items-toolbar">
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="me-3 change-order-items-toolbar-button">
                    Add Custom Change
                </Button>
                <Form.Check 
                    type="checkbox" 
                    label="Show All Items" 
                    checked={showAllItems} 
                    onChange={(e) => setShowAllItems(e.target.checked)}
                    className="d-inline-block change-order-items-show-all"
                />
            </div>
            {localChangeOrderDetails.map((detail, index) => (
                <Form
                    key={index}
                    className="mb-4 p-3 border rounded position-relative change-order-item-card"
                    style={{ backgroundColor: detail.FollowUpRequired ? '#ffcccc' : 'transparent' }} // Apply light red background if Follow Up is required
                >
                    <Row className="align-items-center mb-2 change-order-item-header-grid">
                        <Col md={2} className="change-order-item-status">
                            <span className="change-order-field-label">Status</span>
                            <span className="change-order-item-status-value">{detail.Status || "BLANK"}</span>
                        </Col>
                        <Col md={6} className="change-order-item-title">
                            <Form.Label className="change-order-responsive-label">Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={detail.Title || "BLANK"}
                                onChange={(e) => handleTitleChange(detail.ID, e.target.value)}
                                onBlur={(e) => handleTitleBlur(detail.ID, e.target.value)}
                                disabled={detail.Status === "Resolved"} // Disable if resolved
                            />
                        </Col>
                        <Col md={3} className="change-order-item-follow-up">
                            <label className="form-check change-order-checkbox-tile" htmlFor={`follow-up-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`follow-up-${detail.ID}`}
                                    checked={detail.FollowUpRequired}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'FollowUpRequired', e.target.checked)}
                                    disabled={detail.Status === "Resolved"}
                                />
                                <span className="form-check-label">Follow up</span>
                            </label>
                        </Col>
                        <Col md={1} className="text-end change-order-item-delete-cell">
                            <button type="button" className="btn btn-link text-danger p-0 change-order-item-delete" hidden={detail.Status === "Resolved"} onClick={() => handleDelete(detail.ID)} aria-label={`Delete ${detail.Title || "change order item"}`}>
                                <i className="bi bi-x-circle"></i>
                            </button>
                        </Col>
                    </Row>
                    <Row className="align-items-center mb-2 change-order-item-details-grid">
                        <Col md={4}>
                            <Form.Group controlId={`reasonForChange-${index}`}>
                                <Form.Label>Reason For Change</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={detail.ReasonForChange || "BLANK"}
                                    onChange={(e) => handleInputChange(detail.ID, 'ReasonForChange', e.target.value)}
                                    onBlur={(e) => handleInputChange(detail.ID, 'ReasonForChange', e.target.value)}
                                    disabled={detail.Status === "Resolved"} // Disable if resolved
                                >
                                    <option value="BLANK">Select Reason For Change</option>
                                    {reasonForChangeOptions.map((option) => (
                                        <option key={option.ID} value={option.DisplayText}>
                                            {option.DisplayText}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="change-order-item-checkboxes">
                            <Form.Label className="change-order-responsive-label">CSO</Form.Label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`impacts-cso-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`impacts-cso-${detail.ID}`}
                                    checked={detail.ImpactsCSO}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'ImpactsCSO', e.target.checked)}
                                    disabled={detail.Status === "Resolved"}
                                />
                                <span className="form-check-label">Impacts CSO</span>
                            </label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`cso-updated-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`cso-updated-${detail.ID}`}
                                    checked={detail.CSOUpdated}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'CSOUpdated', e.target.checked)}
                                    disabled={detail.Status === "Resolved"}
                                />
                                <span className="form-check-label">CSO Updated</span>
                            </label>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId={`billCreditTo-${index}`}>
                                <Form.Label>Bill Credit To</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={detail.BillCreditTo || "BLANK"}
                                    onChange={(e) => handleInputChange(detail.ID, 'BillCreditTo', e.target.value)}
                                    onBlur={(e) => handleInputChange(detail.ID, 'BillCreditTo', e.target.value)}
                                    disabled={detail.Status === "Resolved"} // Disable if resolved
                                >
                                    <option value="BLANK">Select Bill Credit To</option>
                                    {billCreditToOptions.map((option) => (
                                        <option key={option.ID} value={option.DisplayText}>
                                            {option.DisplayText}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="align-items-center mb-2 change-order-item-values-grid">
                        <Col md={6}>
                            <Form.Group controlId={`existingValue-${index}`}>
                                <Form.Label>Existing Value</Form.Label>
                                <Form.Control as="textarea" value={detail.ExistingValue || "BLANK"} className="border" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId={`newValue-${index}`}>
                                <Form.Label>New Value</Form.Label>
                                <Form.Control as="textarea" value={detail.NewValue || "BLANK"} className="border" disabled />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="align-items-center change-order-item-actions-grid">
                        <Col md={8} className="change-order-action-required">
                            <Form.Label className="me-2 change-order-responsive-label">Action Required</Form.Label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`action-required-mechanical-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`action-required-mechanical-${detail.ID}`}
                                    checked={detail.ActionRequiredMechanical}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'ActionRequiredMechanical', e.target.checked)}
                                    disabled={detail.Status === "Resolved" || !projectDetails[0]?.MechanicalEngineer}
                                />
                                <span className="form-check-label">Mechanical</span>
                            </label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`action-required-electrical-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`action-required-electrical-${detail.ID}`}
                                    checked={detail.ActionRequiredElectrical}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'ActionRequiredElectrical', e.target.checked)}
                                    disabled={detail.Status === "Resolved" || !projectDetails[0]?.ElectricalEngineer}
                                />
                                <span className="form-check-label">Electrical</span>
                            </label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`action-required-controls-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`action-required-controls-${detail.ID}`}
                                    checked={detail.ActionRequiredControls}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'ActionRequiredControls', e.target.checked)}
                                    disabled={detail.Status === "Resolved" || !projectDetails[0]?.ControlsEngineer}
                                />
                                <span className="form-check-label">Controls</span>
                            </label>
                            <label className="form-check form-check-inline change-order-checkbox-tile" htmlFor={`action-required-csm-${detail.ID}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`action-required-csm-${detail.ID}`}
                                    checked={detail.ActionRequiredCSM}
                                    onChange={(e) => handleCheckboxChange(detail.ID, 'ActionRequiredCSM', e.target.checked)}
                                    disabled={detail.Status === "Resolved"}
                                />
                                <span className="form-check-label">CSM</span>
                            </label>
                        </Col>
                        <Col md={4} className="change-order-item-co-number"><strong>CO#</strong> {detail.ChangeOrderNumber || "BLANK"}</Col>
                    </Row>
                </Form>
            ))}
            {localChangeOrderDetails.length > 0 && (
                <Button 
                    variant="primary" 
                    className="change-order-populate-button"
                    onClick={async () => {
                        // Fetch the latest change order details before opening the modal
                        console.log('Fetching latest change order details...');
                        await dispatch(fetchFullJob({ jobId }));
                        setShowEditModal(true);
                    }}
                    disabled={isPopulateDisabled} // Disable button based on condition
                >
                    Populate Change Order
                </Button>
            )}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl" className="change-order-modal change-order-edit-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{isPreviewMode ? "Send Change Order" : "Edit Change Order"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isPreviewMode ? (
                        <SendChangeOrder 
                            notes={notes} 
                            onClose={handleModalCloseAfterSubmit} 
                            impactsCSM={impactsCSM} 
                            includeAllControls={includeAllControls} 
                        />
                    ) : (
                        <EditChangeOrder 
                            setNotes={setNotes} 
                            notes={notes}
                            impactsCSM={impactsCSM} 
                            setImpactsCSM={setImpactsCSM} 
                            includeAllControls={includeAllControls} 
                            setIncludeAllControls={setIncludeAllControls} 
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleTogglePreview}>
                        {isPreviewMode ? "Edit Change Order" : "Preview and Send"}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showDuplicateModal} onHide={handleDuplicateModalClose} backdrop={isLoadingDuplicates ? "static" : true} keyboard={!isLoadingDuplicates} className="change-order-modal change-order-duplicate-modal">
                <Modal.Header closeButton={!isLoadingDuplicates}>
                    <Modal.Title>{isLoadingDuplicates ? "Processing Duplicates" : "Duplicate Titles Found"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingDuplicates ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Combining duplicate items and reloading data...</p>
                        </div>
                    ) : (
                        <p>Duplicate item found, do you want to automatically combine?</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!isLoadingDuplicates && (
                        <>
                            <Button 
                                variant="secondary" 
                                onClick={handleDuplicateModalClose} 
                                disabled={userSelection}
                            >
                                No
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleCombineDuplicates} 
                                disabled={userSelection}
                            >
                                Yes
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} className="change-order-modal change-order-add-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Add Change Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="formTitle">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={newChangeOrder.Title || ""}
                                onChange={(e) => setNewChangeOrder({ ...newChangeOrder, Title: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group controlId="formReasonForChange">
                            <Form.Label>Reason for Change</Form.Label>
                            <Form.Control
                                as="select"
                                value={newChangeOrder.ReasonForChange || ""}
                                onChange={(e) => setNewChangeOrder({ ...newChangeOrder, ReasonForChange: e.target.value })}
                            >
                                <option value="">Select Reason For Change</option>
                                {reasonForChangeOptions.map((option) => (
                                    <option key={option.ID} value={option.DisplayText}>
                                        {option.DisplayText}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="formChange">
                            <Form.Label>Change</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={newChangeOrder.Change || ""}
                                onChange={(e) => setNewChangeOrder({ ...newChangeOrder, Change: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddChangeOrder}>
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ChangeOrderItems;
